<template>
  <section class="mx-auto flex min-h-[70vh] max-w-3xl flex-col gap-4 px-0 py-0 sm:px-4 sm:py-6">
    <div v-if="postLoading" class="border-y border-white/10 bg-midnight-800/60 sm:rounded-3xl sm:border sm:shadow-glow animate-pulse">
      <div class="flex items-center gap-3 px-4 py-4 sm:px-6 sm:pt-6">
        <div class="h-12 w-12 rounded-2xl bg-white/10"></div>
        <div class="space-y-2">
          <div class="h-3 w-28 rounded bg-white/10"></div>
          <div class="h-2 w-20 rounded bg-white/10"></div>
        </div>
        <div class="ml-auto h-2 w-16 rounded bg-white/10"></div>
      </div>
      <div class="aspect-square w-full bg-white/5 sm:mx-6 sm:mt-4 sm:rounded-3xl"></div>
      <div class="space-y-3 px-4 py-4 sm:px-6">
        <div class="h-3 w-2/3 rounded bg-white/10"></div>
        <div class="h-3 w-1/2 rounded bg-white/10"></div>
      </div>
      <div class="mt-6 rounded-t-2xl border-t border-white/10 bg-midnight-900/40 p-4 sm:m-6 sm:rounded-2xl sm:border sm:border-white/10 sm:p-5">
        <div class="space-y-4">
          <div v-for="item in 3" :key="`comment-skeleton-${item}`" class="space-y-3">
            <div class="flex items-center gap-3">
              <div class="h-10 w-10 rounded-full bg-white/10"></div>
              <div class="flex-1 space-y-2">
                <div class="h-3 w-32 rounded bg-white/10"></div>
                <div class="h-2 w-24 rounded bg-white/10"></div>
              </div>
            </div>
            <div class="h-3 w-full rounded bg-white/10"></div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="post" class="border-y border-white/10 bg-midnight-800/60 sm:rounded-3xl sm:border sm:shadow-glow">
      <div class="flex items-center gap-3 px-4 py-4 sm:px-6 sm:pt-6">
        <div class="h-12 w-12 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
          <img
            v-if="authorAvatar"
            :src="authorAvatar"
            alt="Author avatar"
            class="h-full w-full object-cover"
            referrerpolicy="no-referrer"
            crossorigin="anonymous"
          />
          <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
            {{ authorInitials }}
          </div>
        </div>
        <div>
          <div class="flex items-center gap-1.5">
            <p class="text-sm font-semibold text-white">{{ authorName }}</p>
            <verified-badge v-if="profile.verified" size="xs" />
          </div>
          <router-link class="text-xs text-steel-400 hover:text-white" :to="`/u/${authorHandle}`">
            @{{ authorHandle }}
          </router-link>
        </div>
        <span class="ml-auto text-xs text-steel-400">{{ createdLabel }}</span>
      </div>

      <div class="overflow-hidden bg-midnight-900/70 sm:mx-6 sm:mt-4 sm:rounded-3xl sm:border sm:border-white/10">
        <div
          class="relative aspect-square w-full overflow-hidden"
          :style="{ touchAction: imageScale > 1 ? 'none' : 'pan-y' }"
          @touchstart="handleImageTouchStart"
          @touchmove="handleImageTouchMove"
          @touchend="handleImageTouchEnd"
          @touchcancel="handleImageTouchEnd"
        >
          <transition :name="mediaDirection === 'prev' ? 'media-swipe-prev' : 'media-swipe-next'">
            <div :key="postImageUrl" class="media-slide-layer">
              <img
                :src="postImageUrl"
                alt="Post"
                class="post-zoom-image h-full w-full object-cover"
                :class="{ 'post-zoom-image--active': isPanningImage || isPinching }"
                :style="postImageStyle"
              />
            </div>
          </transition>
          
          <!-- Prev button (desktop only) -->
          <button
            v-if="post.mediaUrls?.length > 1"
            type="button"
            class="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-midnight-900/70 text-white hover:border-white hover:bg-midnight-900/90 transition z-20"
            @click.stop="navigateToPrevious"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          
          <!-- Next button (desktop only) -->
          <button
            v-if="post.mediaUrls?.length > 1"
            type="button"
            class="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-midnight-900/70 text-white hover:border-white hover:bg-midnight-900/90 transition z-20"
            @click.stop="navigateToNext"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          
          <!-- Dot indicators -->
          <div v-if="post.mediaUrls?.length > 1" class="absolute bottom-3 left-1/2 z-30 -translate-x-1/2 flex items-center gap-1">
            <button
              v-for="(url, idx) in post.mediaUrls"
              :key="idx"
              type="button"
              class="h-1 rounded-full transition-all"
              :class="idx === post.mediaIndex ? 'w-6 bg-white' : 'w-1 bg-white/40'"
              @click="setMediaIndex(idx)"
            />
          </div>
        </div>
      </div>

      <div v-if="postMetaChips.length" class="mt-4 flex flex-wrap gap-2 px-4 sm:px-6">
        <span
          v-for="chip in postMetaChips"
          :key="chip"
          class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-steel-300"
        >
          {{ chip }}
        </span>
      </div>

      <p v-if="post.caption" class="mt-4 whitespace-pre-line break-words px-4 text-sm text-steel-200/90 sm:px-6">
        {{ post.caption }}
      </p>
      <p v-if="post.description" class="mt-2 whitespace-pre-line break-words px-4 text-sm text-steel-400 sm:px-6">
        {{ post.description }}
      </p>
      <p v-if="post.modifications" class="mt-2 whitespace-pre-line break-words px-4 text-sm text-steel-400 sm:px-6">
        {{ $t('postPage.mods') }}: {{ post.modifications }}
      </p>
      <div v-if="post.engine || post.hp_stock || post.hp_tuned || post.year" class="mt-2 px-4 text-sm text-steel-400 sm:px-6">
        <p v-if="post.engine">{{ $t('postPage.engine') }}: {{ post.engine }}</p>
        <p v-if="post.hp_stock">{{ $t('postPage.hpStock') }}: {{ post.hp_stock }}</p>
        <p v-if="post.hp_tuned">{{ $t('postPage.hpTuned') }}: {{ post.hp_tuned }}</p>
        <p v-if="post.year">{{ $t('postPage.year') }}: {{ post.year }}</p>
      </div>

      <div class="mt-4 flex items-center gap-3 px-4 sm:px-6">
        <button
          type="button"
          class="flex items-center gap-2 text-xs uppercase tracking-[0.3em]"
          :class="postIsLiked ? 'text-rose-500' : 'text-white'"
          @click="togglePostLike"
        >
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path
              d="M12 21s-6.7-4.3-9.3-7.6C.5 10.8 1 7.6 3 5.7c2-2 5.2-1.6 7 0l2 1.9 2-1.9c1.8-1.6 5-2 7 0 2 1.9 2.5 5.1.3 7.7C18.7 16.7 12 21 12 21z"
            />
          </svg>
          {{ postLikeCount }}
        </button>
        <button
          type="button"
          class="text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
          @click="openLikesModal"
        >
          {{ $t('postPage.likes') }}
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs transition hover:border-white/30 hover:text-white"
          :class="postIsBookmarked ? 'border-amber-300/40 bg-amber-300/10 text-amber-100' : ''"
          @click="toggleBookmark"
        >
          {{ $t('postPage.save') }} {{ postSaveCount }}
        </button>
        <button
          v-if="canEditPost"
          type="button"
          class="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs transition hover:border-white/30 hover:text-white"
          @click="openEditPostModal"
        >
          Edit
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs transition hover:border-white/30 hover:text-white"
          @click="openReportModal"
        >
          {{ $t('postPage.report') }}
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs transition hover:border-white/30 hover:text-white"
          @click="sharePost"
        >
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 3v13" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M8 7l4-4 4 4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ $t('postPage.share') }}
        </button>
      </div>
      <p class="mt-2 px-4 text-xs text-steel-400 sm:px-6">{{ postViewCount }} {{ $t('postPage.views') }} · {{ postSaveCount }} {{ $t('postPage.saves') }}</p>
      <div class="mt-4 px-4 sm:px-6">
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="reaction in reactionOptions"
            :key="reaction.type"
            type="button"
            class="flex min-h-[56px] items-center justify-between gap-2 rounded-2xl border px-3 py-2 text-left transition"
            :class="reactionButtonClass(reaction.type)"
            @click="togglePostReaction(reaction.type)"
          >
            <span class="min-w-0">
              <span class="block text-[10px] uppercase tracking-[0.26em]">{{ reaction.label }}</span>
              <span class="mt-1 block text-xs opacity-80">{{ reaction.shortLabel }}</span>
            </span>
            <span class="shrink-0 text-sm font-semibold">{{ getReactionCount(reaction.type) }}</span>
          </button>
        </div>
        <p class="mt-2 text-xs text-steel-400">
          <span v-if="postUserReaction">{{ $t('postPage.yourReaction') }}: {{ reactionLabel(postUserReaction) }}</span>
          <span v-else>{{ $t('postPage.chooseVibe') }}</span>
          <span class="ml-2 text-steel-300">{{ postReactionCount }} {{ $t('postPage.reactions') }}</span>
        </p>
      </div>
      <p v-if="statusMessage" class="mt-2 px-4 text-sm text-steel-300 sm:px-6">{{ statusMessage }}</p>

      <div class="mt-6 rounded-t-2xl border-t border-white/10 bg-midnight-900/40 p-4 sm:m-6 sm:rounded-2xl sm:border sm:border-white/10 sm:p-5">
        <p class="text-xs uppercase tracking-[0.3em] text-steel-400">{{ $t('postPage.comments') }}</p>
        <div v-if="comments.length" class="mt-4 space-y-4">
          <div v-for="comment in comments" :key="comment.id" class="text-sm text-steel-200/90">
            <div class="flex flex-col gap-2">
              <div class="min-w-0 flex items-center gap-3">
                <router-link :to="`/u/${comment.authorHandle}`" class="hover:opacity-80 transition flex-shrink-0">
                  <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                    <img
                      v-if="comment.authorAvatar"
                      :src="comment.authorAvatar"
                      alt=""
                      class="h-full w-full object-cover"
                      referrerpolicy="no-referrer"
                      crossorigin="anonymous"
                    />
                    <div v-else class="flex h-full w-full items-center justify-center text-xs font-semibold text-steel-200">
                      {{ getInitials(comment.authorName) }}
                    </div>
                  </div>
                </router-link>
                <router-link :to="`/u/${comment.authorHandle}`" class="hover:opacity-80 transition">
                  <div class="flex items-center gap-1.5">
                    <p class="font-semibold text-white">{{ comment.authorName }}</p>
                    <verified-badge v-if="comment.authorVerified" size="xs" />
                  </div>
                  <p class="text-xs text-steel-400 hover:text-neon-400">@{{ comment.authorHandle }}</p>
                </router-link>
                <p class="text-xs text-steel-400 ml-auto">{{ getRelativeTime(comment.created_at) }}</p>
              </div>
            </div>
            <div v-if="comment.isEditing" class="mt-2 space-y-3">
              <textarea
                v-model.trim="comment.editDraft"
                rows="3"
                class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              ></textarea>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="rounded-full bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.26em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
                  :disabled="comment.isSavingEdit"
                  @click="saveCommentEdit(comment)"
                >
                  {{ comment.isSavingEdit ? 'Saving...' : 'Save' }}
                </button>
                <button
                  type="button"
                  class="rounded-full border border-white/10 px-3 py-2 text-[11px] uppercase tracking-[0.26em] text-steel-300 transition hover:text-white"
                  :disabled="comment.isSavingEdit"
                  @click="cancelCommentEdit(comment)"
                >
                  Cancel
                </button>
              </div>
            </div>
            <p v-else class="mt-2">{{ comment.content }}</p>
            <div class="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                class="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-steel-400 transition hover:text-white"
                @click="comment.replyOpen = !comment.replyOpen"
              >
                Reply
              </button>
              <button
                v-if="canDeleteComment(comment)"
                type="button"
                class="rounded-full border border-rose-200/20 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-rose-200 transition hover:text-rose-100"
                @click="deleteComment(comment.id)"
              >
                Delete
              </button>
              <button
                v-if="canEditComment(comment)"
                type="button"
                class="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-steel-300 transition hover:text-white"
                @click="startCommentEdit(comment)"
              >
                Edit
              </button>
              <div class="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-steel-400 transition hover:text-white">
                <button
                  type="button"
                  class="inline-flex items-center gap-2"
                  @click="toggleCommentLike(comment)"
                >
                  <svg
                    class="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                    :class="comment.isLiked ? 'text-rose-500' : 'text-white'"
                  >
                    <path
                      d="M12 21s-6.7-4.3-9.3-7.6C.5 10.8 1 7.6 3 5.7c2-2 5.2-1.6 7 0l2 1.9 2-1.9c1.8-1.6 5-2 7 0 2 1.9 2.5 5.1.3 7.7C18.7 16.7 12 21 12 21z"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="text-[11px] uppercase tracking-[0.3em] text-steel-300 hover:text-white"
                  @click.stop="openCommentLikesModal(comment)"
                >
                  {{ comment.likeCount }}
                </button>
              </div>
            </div>

            <div
              v-if="comment.replies.length"
              class="mt-3 space-y-3 rounded-2xl border border-white/10 bg-midnight-900/60 p-3"
            >
              <div v-for="reply in comment.replies" :key="reply.id">
                <div class="flex items-center gap-3">
                  <router-link :to="`/u/${reply.authorHandle}`" class="hover:opacity-80 transition flex-shrink-0">
                    <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                      <img
                        v-if="reply.authorAvatar"
                        :src="reply.authorAvatar"
                        alt=""
                        class="h-full w-full object-cover"
                        referrerpolicy="no-referrer"
                        crossorigin="anonymous"
                      />
                      <div v-else class="flex h-full w-full items-center justify-center text-[10px] font-semibold text-steel-200">
                        {{ getInitials(reply.authorName) }}
                      </div>
                    </div>
                  </router-link>
                <div class="flex flex-col gap-2 flex-1">
                  <p class="text-xs text-steel-400">
                      <router-link :to="`/u/${reply.authorHandle}`" class="inline-block hover:opacity-80 transition">
                        <span class="inline-flex items-center gap-1.5">
                          <span class="font-semibold text-white hover:text-neon-400">{{ reply.authorName }}</span>
                          <verified-badge v-if="reply.authorVerified" size="xs" />
                        </span>
                        <span class="hover:text-neon-400">· @{{ reply.authorHandle }}</span>
                      </router-link>
                      <span class="ml-2">{{ getRelativeTime(reply.created_at) }}</span>
                    </p>
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-if="canEditComment(reply)"
                      type="button"
                      class="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-steel-300 transition hover:text-white"
                      @click="startCommentEdit(reply)"
                    >
                      Edit
                    </button>
                    <button
                      v-if="canDeleteComment(reply)"
                      type="button"
                      class="rounded-full border border-rose-200/20 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-rose-200 transition hover:text-rose-100"
                      @click="deleteComment(reply.id)"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="reply.isEditing" class="mt-2 space-y-3">
                  <textarea
                    v-model.trim="reply.editDraft"
                    rows="3"
                    class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                  ></textarea>
                  <div class="flex flex-wrap gap-2">
                    <button
                      type="button"
                      class="rounded-full bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.26em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
                      :disabled="reply.isSavingEdit"
                      @click="saveCommentEdit(reply)"
                    >
                      {{ reply.isSavingEdit ? 'Saving...' : 'Save' }}
                    </button>
                    <button
                      type="button"
                      class="rounded-full border border-white/10 px-3 py-2 text-[11px] uppercase tracking-[0.26em] text-steel-300 transition hover:text-white"
                      :disabled="reply.isSavingEdit"
                      @click="cancelCommentEdit(reply)"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                <p v-else class="text-sm text-steel-200/90 mt-2">{{ reply.content }}</p>
              </div>
            </div>

            <form
              v-if="comment.replyOpen"
              class="mt-3 flex flex-col gap-3 sm:flex-row"
              @submit.prevent="addReply(comment)"
            >
              <input
                v-model.trim="comment.newReply"
                type="text"
                placeholder="Write a reply..."
                class="flex-1 rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              />
              <button
                type="submit"
                class="rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400"
              >
                Reply
              </button>
            </form>
          </div>
        </div>
        <p v-else class="mt-4 text-sm text-steel-400">No comments yet.</p>

        <form class="mt-4 flex flex-col gap-3 sm:flex-row" @submit.prevent="addComment">
          <input
            v-model.trim="newComment"
            type="text"
            placeholder="Write a comment..."
            class="flex-1 rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
          />
          <button
            type="submit"
            class="rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400"
          >
            Comment
          </button>
        </form>
      </div>
    </div>

    <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
      {{ errorMessage }}
    </p>

    <div
      v-if="showEditPostModal"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="closeEditPostModal"
    >
      <div class="w-full max-w-3xl max-h-[90vh] overflow-auto rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between gap-4">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Edit post</p>
            <h3 class="mt-2 font-display text-xl font-semibold text-white">Update text and details</h3>
          </div>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeEditPostModal"
          >
            Close
          </button>
        </div>

        <div class="mt-5 grid gap-4 sm:grid-cols-2">
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Caption
            <input
              v-model.trim="editPostForm.caption"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            />
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Description
            <textarea
              v-model.trim="editPostForm.description"
              rows="4"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            ></textarea>
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Modifications
            <textarea
              v-model.trim="editPostForm.modifications"
              rows="3"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            ></textarea>
          </label>
          <label class="block text-sm text-steel-200">
            Make
            <input v-model.trim="editPostForm.car_make" type="text" class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
          </label>
          <label class="block text-sm text-steel-200">
            Model
            <input v-model.trim="editPostForm.car_model" type="text" class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
          </label>
          <label class="block text-sm text-steel-200">
            Engine
            <input v-model.trim="editPostForm.engine" type="text" class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
          </label>
          <label class="block text-sm text-steel-200">
            Year
            <input v-model.number="editPostForm.year" type="number" min="1900" max="2100" class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
          </label>
          <label class="block text-sm text-steel-200">
            HP stock
            <input v-model.number="editPostForm.hp_stock" type="number" min="0" class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
          </label>
          <label class="block text-sm text-steel-200">
            HP tuned
            <input v-model.number="editPostForm.hp_tuned" type="number" min="0" class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Tags
            <input
              v-model.trim="editPostForm.tags"
              type="text"
              placeholder="stance, turbo, night-run"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Status
            <input v-model.trim="editPostForm.status" type="text" class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
          </label>
          <label class="flex items-center gap-3 self-end rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-steel-200">
            <input v-model="editPostForm.is_for_sale" type="checkbox" class="h-4 w-4 rounded border-white/20 bg-midnight-900 text-neon-400 focus:ring-neon-400/30" />
            For sale
          </label>
        </div>

        <p v-if="editPostError" class="mt-4 text-sm text-rose-200">{{ editPostError }}</p>

        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            :disabled="editPostSaving"
            @click="closeEditPostModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="editPostSaving"
            @click="savePostEdit"
          >
            {{ editPostSaving ? 'Saving...' : 'Save changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Floating toast notification (shows on mobile too) -->
    <div v-if="statusMessage" class="fixed bottom-6 left-1/2 z-60 -translate-x-1/2 rounded-full bg-neon-400/95 px-4 py-2 text-sm font-semibold text-midnight-900 shadow pointer-events-none">
      {{ statusMessage }}
    </div>

    <div
      v-if="showLikesModal"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="closeLikesModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between">
          <h3 class="font-display text-lg font-semibold text-white">Likes</h3>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeLikesModal"
          >
            Close
          </button>
        </div>
        <p v-if="likesLoading" class="mt-4 text-sm text-steel-300">Loading...</p>
        <p v-else-if="likesError" class="mt-4 text-sm text-rose-200">{{ likesError }}</p>
        <p v-else-if="!likesUsers.length" class="mt-4 text-sm text-steel-300">No likes yet.</p>
        <div v-else class="mt-4 space-y-3">
          <router-link
            v-for="user in likesUsers"
            :key="user.id"
            :to="`/u/${user.handle}`"
            class="flex items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/60 px-3 py-2 hover:border-white/20"
            @click="closeLikesModal"
          >
            <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="user.avatar"
                :src="user.avatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                {{ user.initials }}
              </div>
            </div>
            <div class="min-w-0">
              <div class="flex items-center gap-1.5">
                <p class="truncate text-sm text-white">{{ user.fullName }}</p>
                <verified-badge v-if="user.verified" size="xs" />
              </div>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <div
      v-if="showCommentLikesModal"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="closeCommentLikesModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between">
          <h3 class="font-display text-lg font-semibold text-white">Comment Likes</h3>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeCommentLikesModal"
          >
            Close
          </button>
        </div>
        <p v-if="commentLikesLoading" class="mt-4 text-sm text-steel-300">Loading...</p>
        <p v-else-if="commentLikesError" class="mt-4 text-sm text-rose-200">{{ commentLikesError }}</p>
        <p v-else-if="!commentLikesUsers.length" class="mt-4 text-sm text-steel-300">No likes yet.</p>
        <div v-else class="mt-4 space-y-3">
          <router-link
            v-for="user in commentLikesUsers"
            :key="user.id"
            :to="`/u/${user.handle}`"
            class="flex items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/60 px-3 py-2 hover:border-white/20"
            @click="closeCommentLikesModal"
          >
            <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="user.avatar"
                :src="user.avatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                {{ user.initials }}
              </div>
            </div>
            <div class="min-w-0">
              <div class="flex items-center gap-1.5">
                <p class="truncate text-sm text-white">{{ user.fullName }}</p>
                <verified-badge v-if="user.verified" size="xs" />
              </div>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <div
      v-if="showReportModal"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="closeReportModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between">
          <h3 class="font-display text-lg font-semibold text-white">Report post</h3>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeReportModal"
          >
            Close
          </button>
        </div>
        <p class="mt-3 text-xs text-steel-400">Tell us what is wrong with this post.</p>
        <textarea
          v-model.trim="reportReason"
          rows="4"
          maxlength="500"
          placeholder="Reason (e.g. nude content, spam, harassment)"
          class="mt-3 w-full resize-none rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-rose-400/70 focus:outline-none focus:ring-2 focus:ring-rose-400/30"
        ></textarea>
        <div class="mt-3 flex items-center justify-between">
          <p v-if="reportError" class="text-xs text-rose-300">{{ reportError }}</p>
          <p v-if="reportStatus" class="text-xs text-neon-300">{{ reportStatus }}</p>
          <button
            type="button"
            class="ml-auto rounded-full bg-white px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-rose-300 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="reportSubmitting || !reportReason"
            @click="submitReport"
          >
            {{ reportSubmitting ? 'Sending...' : 'Send report' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl, getPostImageUrl, refreshReadySession, runSupabaseQueryWithRetry } from '@/lib/supabase'
import { emitAppToast } from '@/lib/toast'
import { createUserNotification } from '@/lib/notifications'
import VerifiedBadge from '@/components/VerifiedBadge.vue'

const REACTION_OPTIONS = [
  { type: 'clean', label: 'Clean', shortLabel: 'polished' },
  { type: 'beast', label: 'Beast', shortLabel: 'aggressive' },
  { type: 'dream_spec', label: 'Dream Spec', shortLabel: 'wishlist' },
]

export default {
  name: 'PostView',
  components: {
    VerifiedBadge,
  },
  data() {
    return {
      post: null,
      comments: [],
      errorMessage: '',
      newComment: '',
      userId: '',
      profile: {},
      postLikeCount: 0,
      postIsLiked: false,
      postReactionCounts: {},
      postReactionCount: 0,
      postUserReaction: '',
      postViewCount: 0,
      postSaveCount: 0,
      postIsBookmarked: false,
      postLoading: true,
      showLikesModal: false,
      likesLoading: false,
      likesError: '',
      likesUsers: [],
      showCommentLikesModal: false,
      commentLikesLoading: false,
      commentLikesError: '',
      commentLikesUsers: [],
      showReportModal: false,
      reportReason: '',
      reportSubmitting: false,
      reportError: '',
      reportStatus: '',
      showEditPostModal: false,
      editPostSaving: false,
      editPostError: '',
      editPostForm: {
        caption: '',
        description: '',
        modifications: '',
        engine: '',
        hp_stock: null,
        hp_tuned: null,
        year: null,
        car_make: '',
        car_model: '',
        tags: '',
        status: '',
        is_for_sale: false,
      },
      statusMessage: '',
      touchStartX: 0,
      touchEndX: 0,
      touchStartY: 0,
      touchEndY: 0,
      imageScale: 1,
      imageTranslateX: 0,
      imageTranslateY: 0,
      pinchStartDistance: 0,
      pinchStartScale: 1,
      panStartX: 0,
      panStartY: 0,
      panOriginX: 0,
      panOriginY: 0,
      isPinching: false,
      isPanningImage: false,
      lastTapAt: 0,
      preloadedImages: {},
      mediaDirection: 'next',
      visibilityHandler: null,
      isRefreshingView: false,
    }
  },
  computed: {
    authorName() {
      return this.profile.full_name || 'DRVN Member'
    },
    authorHandle() {
      return this.profile.handle || 'member'
    },
    authorAvatar() {
      return getAvatarUrl({ avatarPath: this.profile.avatar_path, avatarUrl: this.profile.avatar_url })
    },
    authorInitials() {
      return (this.profile.full_name || 'D')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0].toUpperCase())
        .join('')
    },
    createdLabel() {
      return this.post?.created_at ? new Date(this.post.created_at).toLocaleDateString() : ''
    },
    postImageUrl() {
      if (!this.post) return ''
      return this.post.mediaUrls?.[this.post.mediaIndex] || this.post.mediaUrls?.[0] || ''
    },
    postImagePosition() {
      if (!this.post?.mediaOffsets || !this.post.mediaOffsets.length) return '50% 50%'
      const index = this.post.mediaIndex || 0
      const offset = this.post.mediaOffsets[index] || { x: 50, y: 50 }
      return `${offset.x}% ${offset.y}%`
    },
    postImageStyle() {
      return {
        objectPosition: this.postImagePosition,
        transform: `translate3d(${this.imageTranslateX}px, ${this.imageTranslateY}px, 0) scale(${this.imageScale})`,
      }
    },
    reactionOptions() {
      return REACTION_OPTIONS
    },
    postMetaChips() {
      if (!this.post) return []
      const chips = []
      if (this.post.status) chips.push(this.post.status.replace(/_/g, ' '))
      if (this.post.car_make || this.post.car_model) {
        chips.push([this.post.car_make, this.post.car_model].filter(Boolean).join(' '))
      }
      (this.post.tags || []).forEach((tag) => chips.push(`#${tag}`))
      if (this.post.edited_at) chips.push('edited')
      return chips
    },
    canEditPost() {
      return Boolean(this.userId && this.post?.user_id === this.userId)
    },
  },
  async mounted() {
    try {
      await this.refreshViewData()
    } finally {
      this.postLoading = false
    }

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.refreshViewData({ silent: true })
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)
  },
  beforeUnmount() {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
  },
  methods: {
    async refreshViewData(options = {}) {
      if (this.isRefreshingView) return

      const { silent = false } = options
      this.isRefreshingView = true

      try {
        if (!silent) {
          this.postLoading = true
        }

        const session = await refreshReadySession()
        this.userId = session?.user?.id || ''
        await this.fetchPost()
        await this.recordPostView()
        await this.fetchPostViews()
        await this.fetchPostLikes()
        await this.fetchBookmarkState()
        await this.fetchPostReactions()
        await this.fetchComments()
      } finally {
        this.isRefreshingView = false
        if (!silent) {
          this.postLoading = false
        }
      }
    },
    async fetchPostViews() {
      if (!this.post?.id) return

      const { count } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('post_views')
          .select('id', { count: 'exact', head: true })
          .eq('post_id', this.post.id)
      ))

      this.postViewCount = count || 0
    },
    async recordPostView() {
      if (!this.post?.id || !this.userId) return

      await supabase
        .from('post_views')
        .upsert({
          post_id: this.post.id,
          user_id: this.userId,
        }, {
          onConflict: 'post_id,user_id',
          ignoreDuplicates: true,
        })
    },
    async fetchPostLikes() {
      if (!this.post?.id) return
      const { data } = await supabase
        .from('post_likes')
        .select('user_id')
        .eq('post_id', this.post.id)

      this.postLikeCount = data?.length || 0
      this.postIsLiked = Boolean(data?.some((like) => like.user_id === this.userId))
    },
    async fetchBookmarkState() {
      if (!this.post?.id || !this.userId) {
        this.postIsBookmarked = false
      } else {
        const { data } = await supabase
          .from('post_bookmarks')
          .select('post_id')
          .eq('post_id', this.post.id)
          .eq('user_id', this.userId)
          .maybeSingle()

        this.postIsBookmarked = Boolean(data?.post_id)
      }

      const { count } = await supabase
        .from('post_bookmarks')
        .select('post_id', { count: 'exact', head: true })
        .eq('post_id', this.post.id)

      this.postSaveCount = count || 0
    },
    async fetchPostReactions() {
      if (!this.post?.id) return

      const { data, error } = await supabase
        .from('post_reactions')
        .select('user_id, reaction_type')
        .eq('post_id', this.post.id)

      if (error) return

      const counts = REACTION_OPTIONS.reduce((acc, reaction) => ({
        ...acc,
        [reaction.type]: (data || []).filter((entry) => entry.reaction_type === reaction.type).length,
      }), {})

      this.postReactionCounts = counts
      this.postReactionCount = (data || []).length
      this.postUserReaction = (data || []).find((entry) => entry.user_id === this.userId)?.reaction_type || ''
    },
    reactionLabel(type) {
      return REACTION_OPTIONS.find((reaction) => reaction.type === type)?.label || 'Reaction'
    },
    getReactionCount(type) {
      return Number(this.postReactionCounts?.[type] || 0)
    },
    reactionButtonClass(type) {
      if (this.postUserReaction === type) {
        return 'border-neon-400/50 bg-neon-400/10 text-white shadow-[0_0_0_1px_rgba(125,249,255,0.14)]'
      }
      return 'border-white/10 bg-white/[0.03] text-steel-300 hover:border-white/25 hover:bg-white/[0.06] hover:text-white'
    },
    closeLikesModal() {
      this.showLikesModal = false
      this.likesUsers = []
      this.likesError = ''
      this.likesLoading = false
    },
    async openLikesModal() {
      if (!this.post?.id) return
      this.showLikesModal = true
      this.likesLoading = true
      this.likesError = ''
      this.likesUsers = []

      const { data: likes, error } = await supabase
        .from('post_likes')
        .select('user_id')
        .eq('post_id', this.post.id)

      if (error) {
        this.likesError = 'Unable to load likes.'
        this.likesLoading = false
        return
      }

      const userIds = (likes || []).map((like) => like.user_id).filter(Boolean)
      if (!userIds.length) {
        this.likesUsers = []
        this.likesLoading = false
        return
      }

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, handle, full_name, avatar_url, avatar_path, verified')
        .in('id', userIds)

      if (profileError) {
        this.likesError = 'Unable to load likes.'
        this.likesLoading = false
        return
      }

      const profileMap = new Map((profiles || []).map((profile) => [profile.id, profile]))

      this.likesUsers = userIds.map((userId) => {
        const profile = profileMap.get(userId) || {}
        const avatar = getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url })
        const fullName = profile.full_name || 'DRVN Member'
        const handle = profile.handle || 'member'
        const initials = fullName
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((chunk) => chunk[0].toUpperCase())
          .join('')
        return {
          id: userId,
          handle,
          fullName,
          avatar,
          initials,
          verified: Boolean(profile.verified),
        }
      })
      this.likesLoading = false
    },
    async toggleBookmark() {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to save posts.'
        return
      }
      if (!this.post?.id) return

      if (this.postIsBookmarked) {
        const { error } = await supabase
          .from('post_bookmarks')
          .delete()
          .eq('post_id', this.post.id)
          .eq('user_id', this.userId)

        if (error) {
          this.errorMessage = error.message || 'Unable to update saved posts.'
          return
        }

        this.postIsBookmarked = false
        this.postSaveCount = Math.max(0, this.postSaveCount - 1)
        emitAppToast({ title: 'Removed from saved', message: 'This post is no longer in your saved list.', tone: 'info' })
        return
      }

      const { error } = await supabase
        .from('post_bookmarks')
        .insert({
          post_id: this.post.id,
          user_id: this.userId,
        })

      if (error) {
        this.errorMessage = error.message || 'Unable to save this post.'
        return
      }

      this.postIsBookmarked = true
      this.postSaveCount += 1
      emitAppToast({ title: 'Saved', message: 'Post added to your saved builds.', tone: 'success' })
      await this.notifyPostOwner('post_save', this.post.id)
    },
    async togglePostLike() {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to like posts.'
        return
      }

      if (!this.post?.id) return

      if (this.postIsLiked) {
        await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', this.post.id)
          .eq('user_id', this.userId)
        this.postIsLiked = false
        this.postLikeCount = Math.max(0, this.postLikeCount - 1)
      } else {
        await supabase.from('post_likes').insert({
          post_id: this.post.id,
          user_id: this.userId,
        })
        this.postIsLiked = true
        this.postLikeCount += 1
        await this.notifyPostOwner('post_like', this.post.id)
      }
    },
    async togglePostReaction(reactionType) {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to react to posts.'
        return
      }

      if (!this.post?.id) return

      const previousReaction = this.postUserReaction
      this.errorMessage = ''

      if (previousReaction === reactionType) {
        const { error } = await supabase
          .from('post_reactions')
          .delete()
          .eq('post_id', this.post.id)
          .eq('user_id', this.userId)

        if (error) {
          this.errorMessage = error.message || 'Unable to remove reaction.'
          return
        }

        this.postReactionCounts = {
          ...this.postReactionCounts,
          [reactionType]: Math.max(0, this.getReactionCount(reactionType) - 1),
        }
        this.postReactionCount = Math.max(0, this.postReactionCount - 1)
        this.postUserReaction = ''
        return
      }

      const { error } = await supabase
        .from('post_reactions')
        .upsert({
          post_id: this.post.id,
          user_id: this.userId,
          reaction_type: reactionType,
        }, {
          onConflict: 'post_id,user_id',
        })

      if (error) {
        this.errorMessage = error.message || 'Unable to save reaction.'
        return
      }

      const nextCounts = {
        ...this.postReactionCounts,
      }

      if (previousReaction) {
        nextCounts[previousReaction] = Math.max(0, Number(nextCounts[previousReaction] || 0) - 1)
      } else {
        this.postReactionCount += 1
      }

      nextCounts[reactionType] = Number(nextCounts[reactionType] || 0) + 1
      this.postReactionCounts = nextCounts
      this.postUserReaction = reactionType

      await this.notifyPostOwner(`post_reaction_${reactionType}`, this.post.id)
    },
    async sharePost() {
      this.errorMessage = ''
      this.statusMessage = ''
      const postUrl = `${window.location.origin}/post/${this.post?.id || ''}`

      const ok = await this.copyTextToClipboard(postUrl)
      if (ok) {
        this.statusMessage = 'Post link copied to clipboard.'
      } else {
        this.errorMessage = 'Unable to copy link. ' + postUrl
      }

      setTimeout(() => (this.statusMessage = ''), 3000)
    },

    async copyTextToClipboard(text) {
      // Prefer modern API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text)
          return true
        } catch (e) {
          // fall back to legacy method
        }
      }

      try {
        const el = document.createElement('textarea')
        el.value = text
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)

        const selection = document.getSelection()
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null
        el.select()
        const success = document.execCommand('copy')
        document.body.removeChild(el)

        if (range && selection) {
          selection.removeAllRanges()
          selection.addRange(range)
        }

        return success
      } catch (e) {
        return false
      }
    },
    getRelativeTime(createdAt) {
      if (!createdAt) return ''
      const now = new Date()
      const created = new Date(createdAt)
      const diffMs = now - created
      const diffMins = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffMins < 60) return `${diffMins}m`
      if (diffHours < 24) return `${diffHours}h`
      return `${diffDays}d`
    },
    getInitials(name) {
      if (!name) return 'D'
      return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0].toUpperCase())
        .join('')
    },
    preloadMedia(urls, index = 0) {
      if (!urls || !urls.length) return
      const candidates = [index, index + 1, index - 1]
        .filter((i) => i >= 0 && i < urls.length)
        .map((i) => urls[i])
        .filter(Boolean)

      candidates.forEach((url) => {
        if (this.preloadedImages[url]) return
        const img = new Image()
        img.decoding = 'async'
        img.src = url
        this.preloadedImages[url] = true
      })
    },
    clampImageScale(value) {
      return Math.min(4, Math.max(1, value))
    },
    clampImageTranslation(x, y) {
      if (this.imageScale <= 1) {
        return { x: 0, y: 0 }
      }

      const maxOffset = ((this.imageScale - 1) * window.innerWidth) / 2
      return {
        x: Math.min(maxOffset, Math.max(-maxOffset, x)),
        y: Math.min(maxOffset, Math.max(-maxOffset, y)),
      }
    },
    applyImageTransform(scale, x, y) {
      const nextScale = this.clampImageScale(scale)
      if (nextScale <= 1) {
        this.imageScale = 1
        this.imageTranslateX = 0
        this.imageTranslateY = 0
        return
      }

      const clamped = this.clampImageTranslation(x, y)
      this.imageScale = nextScale
      this.imageTranslateX = clamped.x
      this.imageTranslateY = clamped.y
    },
    resetImageZoom() {
      this.imageScale = 1
      this.imageTranslateX = 0
      this.imageTranslateY = 0
      this.pinchStartDistance = 0
      this.pinchStartScale = 1
      this.panStartX = 0
      this.panStartY = 0
      this.panOriginX = 0
      this.panOriginY = 0
      this.isPinching = false
      this.isPanningImage = false
    },
    distanceBetweenTouches(touches) {
      if (!touches || touches.length < 2) return 0
      const [first, second] = touches
      const diffX = second.clientX - first.clientX
      const diffY = second.clientY - first.clientY
      return Math.hypot(diffX, diffY)
    },
    toggleImageZoom(targetScale = 2.5, touch = null) {
      if (this.imageScale > 1.02) {
        this.resetImageZoom()
        return
      }

      const nextScale = this.clampImageScale(targetScale)
      if (!touch) {
        this.applyImageTransform(nextScale, 0, 0)
        return
      }

      const rect = touch.target?.getBoundingClientRect?.()
      if (!rect) {
        this.applyImageTransform(nextScale, 0, 0)
        return
      }

      const ratioX = ((touch.clientX - rect.left) / rect.width - 0.5) * -1
      const ratioY = ((touch.clientY - rect.top) / rect.height - 0.5) * -1
      const x = ratioX * (rect.width * (nextScale - 1))
      const y = ratioY * (rect.height * (nextScale - 1))
      this.applyImageTransform(nextScale, x, y)
    },
    setMediaIndex(idx) {
      if (!this.post?.mediaUrls || idx < 0 || idx >= this.post.mediaUrls.length) return
      this.mediaDirection = idx > (this.post.mediaIndex || 0) ? 'next' : 'prev'
      this.post.mediaIndex = idx
      this.resetImageZoom()
      this.preloadMedia(this.post.mediaUrls, this.post.mediaIndex || 0)
    },
    handleImageTouchStart(e) {
      if (!e.touches?.length) return

      if (e.touches.length === 2) {
        this.isPinching = true
        this.isPanningImage = false
        this.pinchStartDistance = this.distanceBetweenTouches(e.touches)
        this.pinchStartScale = this.imageScale
        return
      }

      const touch = e.touches[0]
      const now = Date.now()
      if (now - this.lastTapAt < 280) {
        this.toggleImageZoom(2.5, touch)
        this.lastTapAt = 0
        return
      }
      this.lastTapAt = now

      this.touchStartX = touch.clientX
      this.touchStartY = touch.clientY
      this.touchEndX = touch.clientX
      this.touchEndY = touch.clientY

      if (this.imageScale > 1.02) {
        this.isPanningImage = true
        this.panStartX = touch.clientX
        this.panStartY = touch.clientY
        this.panOriginX = this.imageTranslateX
        this.panOriginY = this.imageTranslateY
      } else {
        this.isPanningImage = false
      }
    },
    handleImageTouchMove(e) {
      if (!e.touches?.length) return

      if (e.touches.length === 2) {
        const distance = this.distanceBetweenTouches(e.touches)
        if (!this.pinchStartDistance) {
          this.pinchStartDistance = distance
          this.pinchStartScale = this.imageScale
        }
        const nextScale = this.pinchStartScale * (distance / this.pinchStartDistance)
        this.applyImageTransform(nextScale, this.imageTranslateX, this.imageTranslateY)
        e.preventDefault()
        return
      }

      const touch = e.touches[0]
      this.touchEndX = touch.clientX
      this.touchEndY = touch.clientY

      if (this.isPanningImage && this.imageScale > 1.02) {
        const diffX = touch.clientX - this.panStartX
        const diffY = touch.clientY - this.panStartY
        this.applyImageTransform(this.imageScale, this.panOriginX + diffX, this.panOriginY + diffY)
        e.preventDefault()
      }
    },
    handleImageTouchEnd(e) {
      if (e.touches?.length === 1 && this.isPinching) {
        const touch = e.touches[0]
        this.isPinching = false
        this.isPanningImage = this.imageScale > 1.02
        this.panStartX = touch.clientX
        this.panStartY = touch.clientY
        this.panOriginX = this.imageTranslateX
        this.panOriginY = this.imageTranslateY
        this.pinchStartDistance = 0
        this.pinchStartScale = this.imageScale
        return
      }

      if (e.touches?.length) return

      this.isPinching = false
      this.isPanningImage = false
      this.pinchStartDistance = 0
      this.pinchStartScale = this.imageScale
      this.handleSwipeEnd(e)
    },
    handleSwipeStart(e) {
      this.touchStartX = e.changedTouches[0].clientX
      this.touchStartY = e.changedTouches[0].clientY
    },
    handleSwipeEnd(e) {
      if (this.imageScale > 1.02) return
      this.touchEndX = e.changedTouches[0].clientX
      this.touchEndY = e.changedTouches[0].clientY
      if (!this.post?.mediaUrls || this.post.mediaUrls.length <= 1) return
      
      const swipeThreshold = 50
      const diffX = this.touchStartX - this.touchEndX
      const diffY = Math.abs(this.touchStartY - this.touchEndY)
      
      // Only navigate carousel if horizontal movement is greater than vertical movement
      if (Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > diffY) {
        if (diffX > 0) {
          this.navigateToNext()
        } else {
          this.navigateToPrevious()
        }
      }
    },
    navigateToNext() {
      if (!this.post?.mediaUrls || this.post.mediaUrls.length <= 1) return
      this.mediaDirection = 'next'
      this.resetImageZoom()
      this.post.mediaIndex = (this.post.mediaIndex || 0) + 1
      if (this.post.mediaIndex >= this.post.mediaUrls.length) {
        this.post.mediaIndex = 0
      }
      this.preloadMedia(this.post.mediaUrls, this.post.mediaIndex || 0)
      this.$forceUpdate()
    },
    navigateToPrevious() {
      if (!this.post?.mediaUrls || this.post.mediaUrls.length <= 1) return
      this.mediaDirection = 'prev'
      this.resetImageZoom()
      this.post.mediaIndex = (this.post.mediaIndex || 1) - 1
      if (this.post.mediaIndex < 0) {
        this.post.mediaIndex = this.post.mediaUrls.length - 1
      }
      this.preloadMedia(this.post.mediaUrls, this.post.mediaIndex || 0)
      this.$forceUpdate()
    },
    normalizeTagList(value) {
      if (!value) return []
      if (Array.isArray(value)) {
        return value.map((tag) => String(tag || '').trim()).filter(Boolean)
      }
      return String(value)
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    },
    commentUiState(comment) {
      return {
        ...comment,
        replies: [],
        replyOpen: false,
        newReply: '',
        likeCount: 0,
        isLiked: false,
        isEditing: false,
        isSavingEdit: false,
        editDraft: comment.content || '',
      }
    },
    canEditComment(comment) {
      return Boolean(this.userId && comment?.user_id === this.userId)
    },
    canDeleteComment(comment) {
      return Boolean(this.userId && (comment.user_id === this.userId || this.post?.user_id === this.userId))
    },
    openEditPostModal() {
      if (!this.canEditPost || !this.post) return
      this.editPostError = ''
      this.editPostSaving = false
      this.editPostForm = {
        caption: this.post.caption || '',
        description: this.post.description || '',
        modifications: this.post.modifications || '',
        engine: this.post.engine || '',
        hp_stock: this.post.hp_stock ?? null,
        hp_tuned: this.post.hp_tuned ?? null,
        year: this.post.year ?? null,
        car_make: this.post.car_make || '',
        car_model: this.post.car_model || '',
        tags: (this.post.tags || []).join(', '),
        status: this.post.status || '',
        is_for_sale: Boolean(this.post.is_for_sale),
      }
      this.showEditPostModal = true
    },
    closeEditPostModal() {
      if (this.editPostSaving) return
      this.showEditPostModal = false
      this.editPostError = ''
    },
    async savePostEdit() {
      if (!this.canEditPost || !this.post?.id) return

      this.editPostSaving = true
      this.editPostError = ''

      const payload = {
        caption: this.editPostForm.caption || '',
        description: this.editPostForm.description || '',
        modifications: this.editPostForm.modifications || '',
        engine: this.editPostForm.engine || '',
        hp_stock: Number.isFinite(this.editPostForm.hp_stock) ? this.editPostForm.hp_stock : null,
        hp_tuned: Number.isFinite(this.editPostForm.hp_tuned) ? this.editPostForm.hp_tuned : null,
        year: Number.isFinite(this.editPostForm.year) ? this.editPostForm.year : null,
        car_make: this.editPostForm.car_make || '',
        car_model: this.editPostForm.car_model || '',
        tags: this.normalizeTagList(this.editPostForm.tags),
        status: this.editPostForm.status || 'active',
        is_for_sale: Boolean(this.editPostForm.is_for_sale),
        edited_at: new Date().toISOString(),
      }

      const { error } = await supabase
        .from('posts')
        .update(payload)
        .eq('id', this.post.id)
        .eq('user_id', this.userId)

      this.editPostSaving = false

      if (error) {
        this.editPostError = error.message || 'Unable to save post changes.'
        return
      }

      this.showEditPostModal = false
      emitAppToast({ title: 'Post updated', message: 'Your post changes are now live.', tone: 'success' })
      await this.fetchPost()
    },
    startCommentEdit(comment) {
      if (!this.canEditComment(comment)) return
      comment.isEditing = true
      comment.editDraft = comment.content || ''
    },
    cancelCommentEdit(comment) {
      if (!comment) return
      comment.isEditing = false
      comment.isSavingEdit = false
      comment.editDraft = comment.content || ''
    },
    async saveCommentEdit(comment) {
      if (!this.canEditComment(comment)) return
      const nextContent = String(comment.editDraft || '').trim()
      if (!nextContent) {
        this.errorMessage = 'Comment cannot be empty.'
        return
      }

      comment.isSavingEdit = true
      this.errorMessage = ''

      const { error } = await supabase
        .from('post_comments')
        .update({ content: nextContent })
        .eq('id', comment.id)
        .eq('user_id', this.userId)

      comment.isSavingEdit = false

      if (error) {
        this.errorMessage = error.message || 'Unable to save comment changes.'
        return
      }

      comment.content = nextContent
      comment.isEditing = false
      emitAppToast({ title: 'Comment updated', message: 'Your comment has been edited.', tone: 'success' })
      await this.fetchComments()
    },
    async fetchPost() {
      this.errorMessage = ''
      const postId = this.$route.params.id

        const { data, error } = await runSupabaseQueryWithRetry(() => (
          supabase
            .from('posts')
            .select('id, caption, description, modifications, engine, hp_stock, hp_tuned, year, car_make, car_model, tags, status, edited_at, image_url, image_path, image_offset_x, image_offset_y, created_at, user_id, profiles (handle, full_name, avatar_url, avatar_path, verified), post_media (image_url, image_path, sort_order, offset_x, offset_y)')
            .eq('id', postId)
            .maybeSingle()
        ))

      if (error || !data) {
        this.errorMessage = 'Post not found.'
        return
      }

      const mediaItems = [
        {
          url: data.image_path
            ? getPostImageUrl({ imagePath: data.image_path, imageUrl: data.image_url })
            : data.image_url,
          offset: {
            x: Number.isFinite(data.image_offset_x) ? data.image_offset_x : 50,
            y: Number.isFinite(data.image_offset_y) ? data.image_offset_y : 50,
          },
        },
        ...(data.post_media || []).map((item) => ({
          url: item.image_path
            ? getPostImageUrl({ imagePath: item.image_path, imageUrl: item.image_url })
            : item.image_url,
          offset: {
            x: Number.isFinite(item.offset_x) ? item.offset_x : 50,
            y: Number.isFinite(item.offset_y) ? item.offset_y : 50,
          },
        })),
      ].filter((item) => Boolean(item.url))

      this.post = {
        ...data,
        mediaUrls: mediaItems.map((item) => item.url),
        mediaOffsets: mediaItems.map((item) => item.offset),
        mediaIndex: 0,
      }
      this.resetImageZoom()
      this.preloadMedia(this.post.mediaUrls, this.post.mediaIndex || 0)
      this.profile = data.profiles || {}
    },
    async fetchComments() {
      const postId = this.$route.params.id
      const { data } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('post_comments')
          .select('id, content, created_at, parent_comment_id, user_id, profiles (handle, full_name, avatar_url, avatar_path, verified)')
          .eq('post_id', postId)
          .order('created_at', { ascending: true })
      ))

      const comments = (data || []).map((comment) => {
        const avatar = getAvatarUrl({
          avatarPath: comment.profiles?.avatar_path,
          avatarUrl: comment.profiles?.avatar_url,
        })
        return this.commentUiState({
          ...comment,
          authorName: comment.profiles?.full_name || 'DRVN Member',
          authorHandle: comment.profiles?.handle || 'member',
          authorVerified: Boolean(comment.profiles?.verified),
          authorAvatar: avatar,
        })
      })

      const commentIds = comments.map((comment) => comment.id)
      const { data: likes } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('post_comment_likes')
          .select('comment_id, user_id')
          .in('comment_id', commentIds.length ? commentIds : ['00000000-0000-0000-0000-000000000000'])
      ))

      const likeMap = new Map()
      ;(likes || []).forEach((like) => {
        likeMap.set(`${like.comment_id}:${like.user_id}`, true)
      })

      comments.forEach((comment) => {
        comment.likeCount = (likes || []).filter((like) => like.comment_id === comment.id).length
        comment.isLiked = likeMap.has(`${comment.id}:${this.userId}`)
      })

      const topLevel = []
      const byId = new Map(comments.map((comment) => [comment.id, comment]))
      comments.forEach((comment) => {
        if (comment.parent_comment_id) {
          const parent = byId.get(comment.parent_comment_id)
          if (parent) {
            parent.replies.push(comment)
          }
        } else {
          topLevel.push(comment)
        }
      })

      this.comments = topLevel
    },
    async addComment() {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to comment.'
        return
      }

      if (!this.newComment) return

      const postId = this.$route.params.id
      const { error } = await supabase.from('post_comments').insert({
        post_id: postId,
        user_id: this.userId,
        content: this.newComment,
      })

      if (error) {
        this.errorMessage = error.message
        return
      }

      this.newComment = ''
      await this.notifyPostOwner('post_comment', postId)
      await this.fetchComments()
    },
    async addReply(parentComment) {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to reply.'
        return
      }

      if (!parentComment.newReply) return

      const postId = this.$route.params.id
      const { error } = await supabase.from('post_comments').insert({
        post_id: postId,
        user_id: this.userId,
        parent_comment_id: parentComment.id,
        content: parentComment.newReply,
      })

      if (error) {
        this.errorMessage = error.message
        return
      }

      parentComment.newReply = ''
      parentComment.replyOpen = false
      await this.notifyCommentOwner(parentComment.id)
      await this.fetchComments()
    },
    async toggleCommentLike(comment) {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to like comments.'
        return
      }

      if (comment.isLiked) {
        await supabase
          .from('post_comment_likes')
          .delete()
          .eq('comment_id', comment.id)
          .eq('user_id', this.userId)
        comment.isLiked = false
        comment.likeCount = Math.max(0, comment.likeCount - 1)
      } else {
        await supabase.from('post_comment_likes').insert({
          comment_id: comment.id,
          user_id: this.userId,
        })
        comment.isLiked = true
        comment.likeCount += 1
        await this.notifyCommentOwner(comment.id, 'comment_like')
      }
    },
    closeCommentLikesModal() {
      this.showCommentLikesModal = false
      this.commentLikesUsers = []
      this.commentLikesError = ''
      this.commentLikesLoading = false
    },
    openReportModal() {
      this.reportReason = ''
      this.reportError = ''
      this.reportStatus = ''
      this.showReportModal = true
    },
    closeReportModal() {
      this.showReportModal = false
      this.reportReason = ''
      this.reportError = ''
      this.reportStatus = ''
      this.reportSubmitting = false
    },
    async submitReport() {
      if (!this.userId) {
        this.reportError = 'Please sign in to report posts.'
        return
      }
      if (!this.post?.id) {
        this.reportError = 'Post not found.'
        return
      }
      const reason = this.reportReason.trim()
      if (!reason) return

      this.reportSubmitting = true
      this.reportError = ''
      this.reportStatus = ''

      const { error } = await supabase.from('post_reports').insert({
        post_id: this.post.id,
        reporter_id: this.userId,
        reason,
        category: 'manual',
      })

      this.reportSubmitting = false

      if (error) {
        this.reportError = error.message || 'Unable to submit report.'
        return
      }

      this.reportStatus = 'Report sent. Thank you.'
      emitAppToast({ title: 'Report sent', message: 'Thanks for helping keep DRVN clean.', tone: 'success' })
      setTimeout(() => this.closeReportModal(), 1200)
    },
    async openCommentLikesModal(comment) {
      if (!comment?.id) return
      this.showCommentLikesModal = true
      this.commentLikesLoading = true
      this.commentLikesError = ''
      this.commentLikesUsers = []

      const { data: likes, error } = await supabase
        .from('post_comment_likes')
        .select('user_id')
        .eq('comment_id', comment.id)

      if (error) {
        this.commentLikesError = 'Unable to load likes.'
        this.commentLikesLoading = false
        return
      }

      const userIds = (likes || []).map((like) => like.user_id).filter(Boolean)
      if (!userIds.length) {
        this.commentLikesUsers = []
        this.commentLikesLoading = false
        return
      }

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, handle, full_name, avatar_url, avatar_path, verified')
        .in('id', userIds)

      if (profileError) {
        this.commentLikesError = 'Unable to load likes.'
        this.commentLikesLoading = false
        return
      }

      const profileMap = new Map((profiles || []).map((profile) => [profile.id, profile]))

      this.commentLikesUsers = userIds.map((userId) => {
        const profile = profileMap.get(userId) || {}
        const avatar = getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url })
        const fullName = profile.full_name || 'DRVN Member'
        const handle = profile.handle || 'member'
        const initials = fullName
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((chunk) => chunk[0].toUpperCase())
          .join('')
        return {
          id: userId,
          handle,
          fullName,
          avatar,
          initials,
          verified: Boolean(profile.verified),
        }
      })
      this.commentLikesLoading = false
    },
    async deleteComment(commentId) {
      const { error } = await supabase.from('post_comments').delete().eq('id', commentId)
      if (error) {
        this.errorMessage = error.message
        return
      }
      await this.fetchComments()
    },
    async notifyPostOwner(type, postId) {
      if (!this.post?.user_id || this.post.user_id === this.userId) return
      await createUserNotification({
        recipientId: this.post.user_id,
        actorId: this.userId,
        postId,
        type,
      })
    },
    async notifyCommentOwner(commentId, type = 'comment_reply') {
      const { data } = await supabase
        .from('post_comments')
        .select('user_id')
        .eq('id', commentId)
        .maybeSingle()

      if (!data?.user_id || data.user_id === this.userId) return

      await createUserNotification({
        recipientId: data.user_id,
        actorId: this.userId,
        postId: this.post?.id,
        commentId,
        type,
      })
    },
  },
}
</script>

<style>
.media-slide-layer {
  position: absolute;
  inset: 0;
}

.media-swipe-next-enter-active,
.media-swipe-next-leave-active,
.media-swipe-prev-enter-active,
.media-swipe-prev-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.media-swipe-next-enter-from {
  opacity: 0;
  transform: translateX(10%);
}

.media-swipe-next-leave-to {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-enter-from {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-leave-to {
  opacity: 0;
  transform: translateX(10%);
}

.post-zoom-image {
  transform-origin: center center;
  transition: transform 160ms ease;
  will-change: transform;
}

.post-zoom-image--active {
  transition: none;
}
</style>
